import React, { useEffect, useState } from 'react';
import { fetchBgvInProgressCandidates, fetchCandidateInfo } from '../../services/user/api';
import { Search } from 'lucide-react';
import '../../styles/user/bgvInProgress.css';
import { FaEye } from 'react-icons/fa';

const BgvInProgressCandidatesPage = ({onViewReportTwo }) => {
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  // Modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedCandidateData, setSelectedCandidateData] = useState(null);

  const startIndex = (currentPage - 1) * entriesPerPage;
  const totalPages = Math.ceil(totalElements / entriesPerPage);

  useEffect(() => {
    loadCandidates(currentPage - 1, entriesPerPage, search);
  }, [currentPage, entriesPerPage, search]);

  const loadCandidates = async (page, size, searchTerm = '') => {
    try {
      setIsLoading(true);
      const res = await fetchBgvInProgressCandidates(page, size, searchTerm);
      const data = res.data.data || {};
      setCandidates(data.content || []);
      setTotalElements(data.totalElements || 0);
      setFetchError('');
    } catch (err) {
      console.error(err);
      setFetchError('Failed to load BGV In Progress candidates.');
      setCandidates([]);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const formatDate = (date) => {
  if (!date) return "---";

  const d = new Date(date);

  const day = String(d.getDate()).padStart(2, "0");  
  const month = String(d.getMonth() + 1).padStart(2, "0"); 
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};

  const renderSortIcon = (field) => {
    if (sortField !== field) return ' ⇅';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedCandidates = [...candidates]
    .filter((c) => {
      const searchText = search.toLowerCase();
      return (
        String(c.candidateid || '').toLowerCase().includes(searchText) ||
        String(c.fullname || '').toLowerCase().includes(searchText) ||
        String(c.email || '').toLowerCase().includes(searchText) ||
        String(c.mobileNumber || '').toLowerCase().includes(searchText) ||
        String(c.dateOfBirth || '').toLowerCase().includes(searchText) ||
        String(c.caseInitiatedDateOnlyAsString || '').toLowerCase().includes(searchText) ||
        String(c.caseInitiatedTimeOnly || '').toLowerCase().includes(searchText)
      );
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      let valA = a[sortField] ?? '';
      let valB = b[sortField] ?? '';
      if (sortField.toLowerCase().includes('date') || sortField.toLowerCase().includes('dob')) {
        const parseDate = (d) => {
          if (!d) return 0;
          const [day, month, year] = d.split('-');
          return new Date(year, month - 1, day).getTime();
        };
        valA = parseDate(valA);
        valB = parseDate(valB);
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  // Fetch candidate info and open modal
  const handleViewReport = async (candidateId) => {
    try {
      const res = await fetchCandidateInfo(candidateId);
      if (res.data.status) {
        setSelectedCandidateData(res.data.data);
        setShowReportModal(true);
      } else {
        alert(res.data.message || 'Failed to fetch candidate info');
      }
    } catch (error) {
      console.error(error);
      alert('Error fetching candidate report');
    }
  };

  return (
    <>
      <p className="client-header">BGV In Progress Candidates</p>
      <div className="client-wrapper">
        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{' '}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{' '}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
            <input
             id="client-search-input"
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by any field..."
            />
            <Search className="client-search-icon" size={18} />
          </div>
        </div>
        </div>

        <div className="clinet-table-container">
          {isLoading ? (
            <div className="loading">Loading Candidates...</div>
          ) : fetchError ? (
            <div className="error-message">{fetchError}</div>
          ) : (
            <table className="candidates-clinet-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('candidateid')}>Candidate ID {renderSortIcon('candidateid')}</th>
                  <th onClick={() => handleSort('fullname')}>Name {renderSortIcon('fullname')}</th>
                  <th onClick={() => handleSort('email')}>Email {renderSortIcon('email')}</th>
                  <th onClick={() => handleSort('mobileNumber')}>Mobile Number {renderSortIcon('mobileNumber')}</th>
                  <th onClick={() => handleSort('dateOfBirth')}>DOB {renderSortIcon('dateOfBirth')}</th>
                  <th onClick={() => handleSort('caseInitiatedDateOnly')}>Case Initiated Date {renderSortIcon('caseInitiatedDateOnly')}</th>
                  <th onClick={() => handleSort('caseInitiatedTimeOnly')}>Case Initiated Time {renderSortIcon('caseInitiatedTimeOnly')}</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedCandidates.length > 0 ? (
                  sortedCandidates.map((c) => (
                    <tr key={c.candidateid}>
                      <td>{c.candidateid}</td>
                      <td>{c.fullname}</td>
                      <td>{c.email}</td>
                      <td>{c.mobileNumber}</td>
                      <td>{c.dateOfBirth}</td>
                     <td>{formatDate(c.caseInitiatedDateOnly)}</td>
                      <td>{c.caseInitiatedTimeOnly || 'N/A'}</td>
                      <td>
                        <button
                          className={`icon-btn ${c.bgcStatus === 'PENDING_SUBMISSION' ? 'text-gray-400' : 'text-indigo-600'}`}
                          title="View Report"
                          disabled={c.bgcStatus === 'PENDING_SUBMISSION'}
                         onClick={() => onViewReportTwo(c.candidateid)}
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">No matching candidates found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {totalElements > 0 && (
            <div className="pagination">
              <div className="pagination-summary">
                Showing {totalElements === 0 ? 0 : startIndex + 1} to{' '}
                {Math.min(startIndex + candidates.length, totalElements)} of {totalElements} entries
              </div>
              <div className="pagination-buttons-user">
                <button className="page-btn" onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>←</button>

                <button className={`page-btn ${"active"}`} onClick={() => setCurrentPage(currentPage)}>{currentPage}</button>

                {currentPage < totalPages && (
                  <button className="page-btn" onClick={() => setCurrentPage(currentPage + 1)}>{currentPage + 1}</button>
                )}

                <button className="page-btn" onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>→</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ====== Full Candidate Report Modal ====== */}
      {showReportModal && selectedCandidateData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-btn" onClick={() => setShowReportModal(false)}>×</button>
            <h2 className="modal-title">Candidate Full Report</h2>

            {/* Personal Info */}
            <h3>Personal Info</h3>
            {selectedCandidateData.personalInfoResponse && (
              <div>
                <p><strong>Name:</strong> {selectedCandidateData.personalInfoResponse.firstName} {selectedCandidateData.personalInfoResponse.lastName}</p>
                <p><strong>Email:</strong> {selectedCandidateData.personalInfoResponse.email}</p>
                <p><strong>Mobile:</strong> {selectedCandidateData.personalInfoResponse.mobileNumber}</p>
                <p><strong>DOB:</strong> {selectedCandidateData.personalInfoResponse.dateOfBirth}</p>
                <p><strong>Gender:</strong> {selectedCandidateData.personalInfoResponse.gender}</p>
                <p><strong>Father's Name:</strong> {selectedCandidateData.personalInfoResponse.fatherName}</p>
                <p><strong>Aadhaar:</strong> {selectedCandidateData.personalInfoResponse.aadhaarCardNumber}</p>
                {selectedCandidateData.personalInfoResponse.personalImageFileName && (
                  <p><strong>Personal Image:</strong> <a href={selectedCandidateData.personalInfoResponse.personalImageFileName} target="_blank" rel="noreferrer">View</a></p>
                )}
                {selectedCandidateData.personalInfoResponse.aadhaarProofFileName && (
                  <p><strong>Aadhaar Proof:</strong> <a href={selectedCandidateData.personalInfoResponse.aadhaarProofFileName} target="_blank" rel="noreferrer">View</a></p>
                )}
              </div>
            )}

            {/* Address Info */}
            <h3>Address Info</h3>
            {selectedCandidateData.addressInfoResponse && (
              <div>
                <p><strong>Current:</strong> {selectedCandidateData.addressInfoResponse.currentAddressLine1}, {selectedCandidateData.addressInfoResponse.currentCity}</p>
                {selectedCandidateData.addressInfoResponse.currentAddressFile && (
                  <p><strong>Proof:</strong> <a href={selectedCandidateData.addressInfoResponse.currentAddressFile} target="_blank" rel="noreferrer">View</a></p>
                )}
                <p><strong>Permanent:</strong> {selectedCandidateData.addressInfoResponse.permanentAddressLine1}, {selectedCandidateData.addressInfoResponse.permanentCity}</p>
                {selectedCandidateData.addressInfoResponse.permanentAddressFile && (
                  <p><strong>Proof:</strong> <a href={selectedCandidateData.addressInfoResponse.permanentAddressFile} target="_blank" rel="noreferrer">View</a></p>
                )}
              </div>
            )}

            {/* Education */}
            <h3>Education</h3>
            <ul>
              {selectedCandidateData.educationInfos?.map((edu, idx) => (
                <li key={idx}>
                  <p><strong>{edu.educationLevel}</strong> - {edu.instituteName} ({edu.enrolledYear} to {edu.passingYear})</p>
                  {edu.certificateFileUrl && <a href={edu.certificateFileUrl} target="_blank" rel="noreferrer">View Certificate</a>}
                </li>
              ))}
            </ul>

            {/* Employment */}
            <h3>Employment</h3>
            <ul>
              {selectedCandidateData.employmentInfos?.map((emp, idx) => (
                <li key={idx}>
                  <p><strong>Company:</strong> {emp.companyName} | <strong>Title:</strong> {emp.jobTitle}</p>
                  <p><strong>From:</strong> {emp.workingFrom} <strong>To:</strong> {emp.workingTo}</p>
                  {emp.offerLetterUrl && <a href={emp.offerLetterUrl} target="_blank" rel="noreferrer">View Offer Letter</a>}
                  {emp.experienceLetterUrl && <a href={emp.experienceLetterUrl} target="_blank" rel="noreferrer">View Experience Letter</a>}
                  {emp.form16Url && <a href={emp.form16Url} target="_blank" rel="noreferrer">View Form 16</a>}
                  {emp.form26Url && <a href={emp.form26Url} target="_blank" rel="noreferrer">View Form 26</a>}
                  {emp.bankStatementUrl && <a href={emp.bankStatementUrl} target="_blank" rel="noreferrer">View Bank Statement</a>}
                  {emp.salarySlipsUrl?.split(',').map((url, i) => (
                    <a key={i} href={url.trim()} target="_blank" rel="noreferrer">View Salary Slip {i + 1}</a>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default BgvInProgressCandidatesPage;
